import { ref, computed } from "vue";
import { defineStore } from "pinia";
import {
  HISTORY_CARD_STATUSES,
  ISSUED_CARD_STATUSES,
  PENDING_CARD_STATUSES,
} from "@/utils/cards/constants/statuses";

export const useCardStore = defineStore("card", () => {
  const cards = ref(new Map());
  const currentCardId = ref("");
  // Сводка по картам для дашборда (/v1/cards или cards/hybrid/v1/cards по флагу tmaCardsV3Enabled)
  const cardsBalance = ref(0); // balance_all_cards, USD
  const summaryCards = ref([]);
  // Список карт для CardsIndex из GET /v1/cards: порядок карт как в ответе, общий баланс, лимиты на новые карты
  const cardIds = ref([]);
  const allCardsBalance = ref(0); // balance_all_cards, USD
  const cardsMetadata = ref(null); // { can_create_new, max_cards_allowed, remaining_cards, total_active_cards }
  // /v1/cards/config — продукты карт (имена по flow_type)
  const cardsConfig = ref(null);
  // /v2/cards/providers — цены и лимиты первого пополнения по flow_type
  const cardProducts = ref([]);

  // /v1/cards/personal-data — имя держателя ({ full_name }) для реквизитов
  const personalData = ref(null);
  const setPersonalData = (data) => {
    personalData.value = data ?? null;
  };

  // Email для карты вместо email аккаунта (.ru / Apple relay не подходят), уходит в заявку как substitute_email
  const substituteEmail = ref("");
  const setSubstituteEmail = (email) => {
    substituteEmail.value = email || "";
  };

  // Экран подтверждения закрытия: «Заморозить карту» возвращает на карту, которая сама откроет подтверждение заморозки
  const freezeConfirmCardId = ref("");
  const setFreezeConfirmCardId = (id) => {
    freezeConfirmCardId.value = id || "";
  };

  const secretCardData = ref({
    isShown: false,
    card_number: "",
    cvv: "",
    expiry_date: "",
  });

  const setCardData = (id, data) => {
    if (!id) return;
    cards.value.set(id, data);
  };

  const setCards = (data) => {
    if (!data || !Array.isArray(data.cards)) {
      console.warn("Invalid cards data:", data);
      return;
    }
    data.cards?.forEach((card) => {
      cards.value.set(card.emcd_card_id, {
        emcd_card_id: card.emcd_card_id,
        ...card,
      });
    });
    cardIds.value = data.cards.map((card) => card.emcd_card_id);
    allCardsBalance.value = Number(data.balance_all_cards) || 0;
    cardsMetadata.value = data.metadata ?? null;
    setFirstActiveCard(data.cards);
  };

  const setCardsSummary = (data) => {
    cardsBalance.value = Number(data?.balance_all_cards) || 0;
    summaryCards.value = Array.isArray(data?.cards) ? data.cards : [];
  };

  const setCardsConfig = (data) => {
    cardsConfig.value = data ?? null;
  };

  const setCardProducts = (items) => {
    cardProducts.value = Array.isArray(items) ? items : [];
  };

  const getCardProduct = (flowType) =>
    cardProducts.value.find((product) => product.flow_type === flowType);

  const setFirstActiveCard = (cardList = []) => {
    const activeCard = cardList.find((card) => card.status === "active");
    if (activeCard) {
      currentCardId.value = activeCard.emcd_card_id;
      return;
    }

    const inactiveCard = cardList.find((card) => card.status === "inactive");
    if (inactiveCard) {
      currentCardId.value = inactiveCard.emcd_card_id;
    }
  };

  const setSecretData = (data) => {
    secretCardData.value = {
      isShown: true,
      ...data,
    };
  };

  const clearSecretData = () => {
    secretCardData.value = {
      isShown: false,
      card_number: "",
      cvv: "",
      expiry_date: "",
    };
  };

  const allCards = computed(() => Array.from(cards.value.values()));

  // id карт для общей истории операций из последнего GET /v1/cards
  const historyCardIds = computed(() =>
    cardIds.value.filter((id) =>
      HISTORY_CARD_STATUSES.includes(cards.value.get(id)?.status),
    ),
  );

  // Выпущенные карты из последнего GET /v1/cards; данные — из Map, там они свежее после getCardInfo
  const issuedCards = computed(() =>
    cardIds.value
      .map((id) => cards.value.get(id))
      .filter((card) => ISSUED_CARD_STATUSES.includes(card?.status)),
  );

  // Карты в стопке CardsIndex: выпущенные и выпускаемые, в порядке ответа
  const stackCards = computed(() =>
    cardIds.value
      .map((id) => cards.value.get(id))
      .filter(
        (card) =>
          ISSUED_CARD_STATUSES.includes(card?.status) ||
          PENDING_CARD_STATUSES.includes(card?.status),
      ),
  );

  const getCardData = (id) => cards.value.get(id) ?? undefined;

  const getActiveCardId = () => currentCardId.value;

  const getSecretData = () => {
    return secretCardData.value;
  };

  return {
    freezeConfirmCardId,
    setFreezeConfirmCardId,
    allCards,
    issuedCards,
    stackCards,
    historyCardIds,
    allCardsBalance,
    cardsMetadata,
    cardsBalance,
    summaryCards,
    cardsConfig,
    cardProducts,
    personalData,
    setPersonalData,
    substituteEmail,
    setSubstituteEmail,
    setCards,
    setCardsSummary,
    setCardsConfig,
    setCardProducts,
    getCardProduct,
    setCardData,
    getCardData,
    setSecretData,
    clearSecretData,
    getSecretData,
    getActiveCardId,
  };
});
